import { useState } from 'react';
import type { BoardroomFilings } from '../../../types';

const KIND_WORDS: Record<string, string> = {
  SELLS: 'sold by',
  INSTANCE_OF: 'a kind of',
  HAS_FEATURE: 'a feature of',
  PRICES: 'a plan of',
  ADDRESSES: 'addressed by',
  ABOUT: 'a complaint about',
};

function when(at: string | null): string {
  if (!at) return '';
  return new Date(at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * "WHY IS THIS HERE?" A product under the wrong company (SimpleKPI Dashboard
 * under PracticeVital, room 41) could only be traced by matching timestamps
 * against the seats' tool calls. This says what the item is filed under, who
 * filed it, in which dig, from what page, and when.
 */
export default function WhyHere({
  itemId,
  focusParentId,
  load,
  onOpen,
}: {
  itemId: number;
  /** The filing this page is about (the company whose page this is), shown first. */
  focusParentId?: number;
  load: (itemId: number) => Promise<BoardroomFilings>;
  onOpen: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<BoardroomFilings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    // EVERY TIME IT OPENS, not the first time only: a dig can file it
    // somewhere new after it was first opened. The last answer stays on
    // screen while the new one loads.
    if (next) {
      try {
        setData(await load(itemId));
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not load why it is here');
      }
    }
  };
  const rows = (data?.filings ?? []).slice().sort((a, b) => Number(b.parent.id === focusParentId) - Number(a.parent.id === focusParentId));
  return (
    <>
      <button
        type="button"
        onClick={toggle}
        className="text-xs text-gray-500 hover:underline dark:text-gray-400"
        aria-expanded={open}
        data-testid={`why-here-${itemId}`}
      >
        why is this here?
      </button>
      {open && (
        <div className="mt-1 w-full rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200" data-testid={`why-here-detail-${itemId}`}>
          {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
          {!data && !error && <p className="text-gray-400">Reading…</p>}
          {data && rows.length === 0 && <p>Not filed under anything yet.</p>}
          {data && rows.length > 0 && (
            <ul className="space-y-1">
              {rows.map(f => (
                <li key={f.link_id} className={f.parent.id === focusParentId ? 'font-medium' : undefined}>
                  {KIND_WORDS[f.kind] ?? f.kind.toLowerCase()}{' '}
                  <button type="button" onClick={() => onOpen(f.parent.id)} className="text-indigo-600 hover:underline dark:text-indigo-400">
                    {f.parent.title}
                  </button>
                  <span className="font-normal text-gray-500 dark:text-gray-400">
                    {' '}
                    - filed {when(f.at)}
                    {f.tracked ? (
                      <>
                        {f.by && ` by ${f.by.name}`}
                        {f.run && ` in dig #${f.run.id}`}
                        {f.via && (
                          <>
                            , from{' '}
                            <button type="button" onClick={() => onOpen(f.via!.id)} className="text-indigo-600 hover:underline dark:text-indigo-400" title={f.via.url ?? undefined}>
                              {f.via.title}
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <>; who filed it was not recorded (filed before this was tracked)</>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {data?.written_by && (
            <p className="mt-1 text-gray-500 dark:text-gray-400">First written down by {data.written_by.name}.</p>
          )}
        </div>
      )}
    </>
  );
}
